import { RequestErrorDataApi } from "../errors"
import { attachmentConnectorAPI, servicesAPI, dataAPI } from "./api"
import { Action } from "redux"
import {
    AttachmentDTO,
    AttachmentCreatorDTO,
    AttachmentsResponse,
    AttachmentsResponseDataApi,
} from "./models/attachments"
import { ServicesResponse } from "./models/api/services"
import { DataApiQueryOptions, Receiver } from "./models/api/api"
import { ILinkedDocument } from "../components/inlineattachments"

export const getInlineAttachments = (
    itemId: number,
    customFieldDefinitionId: string,
): Promise<ILinkedDocument[]> =>
    servicesAPI.postJSON(
        "GetCustomFieldAttachments",
        { itemId, customFieldDefinitionId },
        parsingJSONCallback,
    )

export const addInlineAttachment = (
    itemId: number,
    customFieldDefinitionId: string,
    formData: FormData,
): Promise<Action> =>
    attachmentConnectorAPI.postWithFormData(
        `?itemId=${itemId}&cfId=${customFieldDefinitionId}`,
        formData,
    )

export const getAttachments = (
    itemId: number,
    encodeDescription: boolean,
): Promise<ILinkedDocument[]> =>
    servicesAPI.postJSON(
        "GetAttachments",
        { itemId, encodeDescription },
        parsingJSONCallback,
    )

export const addAttachment = (
    itemId: number,
    formData: FormData,
): Promise<Action> =>
    attachmentConnectorAPI.postWithFormData(`?itemId=${itemId}`, formData)

export const removeAttachment = (linkedDocumentId: number): Promise<Action> =>
    servicesAPI.postJSON("RemoveAttachmentFromItem", { linkedDocumentId })

const parsingJSONCallback = (
    json: ServicesResponse<AttachmentsResponse>,
): ILinkedDocument[] => (!!json.d.Value ? json.d.Value : [])

const prepareFileUploadDataApi = async (
    file: File,
): Promise<{
    headers: Headers
    body: ArrayBuffer
}> => {
    const headers = new Headers()
    headers.append(
        "Content-Disposition",
        `attachment; size=${file.size}; filename="${file.name}"`,
    )
    headers.append("Content-Type", `${file.type}`)
    const blob = await file.arrayBuffer()
    return { headers, body: blob }
}

export const addAttachmentDataApi = async (
    itemId: number,
    file: File,
    queryParameters?: DataApiQueryOptions,
    receiver?: Receiver,
): Promise<AttachmentDTO> => {
    const { headers, body } = await prepareFileUploadDataApi(file)
    try {
        return await dataAPI.postBody({
            body: body,
            headers,
            path: `Items(${itemId})/Attachments`,
            queryParameters,
            receiver,
        })
    } catch (error) {
        if (error instanceof Response) {
            const errorMessage = error instanceof Error ? error.message : ""
            throw await RequestErrorDataApi.create(
                errorMessage,
                error.status,
                error,
            )
        }
        throw error
    }
}

export const addInlineAttachmentDataApi = async (
    itemId: number,
    fieldId: string,
    file: File,
    queryParameters?: DataApiQueryOptions,
    receiver?: Receiver,
): Promise<AttachmentDTO> => {
    const { headers, body } = await prepareFileUploadDataApi(file)
    try {
        return await dataAPI.postBody({
            body: body,
            headers,
            path: `ItemFields(itemid=${itemId},fieldDefinitionId='${fieldId}')/TF.AttachmentsItemField/Value`,
            queryParameters,
            receiver,
        })
    } catch (error) {
        if (error instanceof Response) {
            const errorMessage = error instanceof Error ? error.message : ""
            throw await RequestErrorDataApi.create(
                errorMessage,
                error.status,
                error,
            )
        }
        throw error
    }
}

export const removeInlineAttachmentDataApi = async (
    itemId: number,
    fieldId: string,
    attachmentId: number,
    queryParameters?: DataApiQueryOptions,
    receiver?: Receiver,
): Promise<void> => {
    try {
        return await dataAPI.remove(
            `ItemFields(itemid=${itemId},fieldDefinitionId='${fieldId}')/TF.AttachmentsItemField/Value/${attachmentId}/$ref`,
            queryParameters,
            receiver,
        )
    } catch (error) {
        if (error instanceof Response) {
            const errorMessage = error instanceof Error ? error.message : ""
            throw await RequestErrorDataApi.create(
                errorMessage,
                error.status,
                error,
            )
        }
        throw error
    }
}

export const getAttachmentsDataApi = async (
    itemId: number,
    queryParameters?: DataApiQueryOptions,
    receiver?: Receiver,
): Promise<AttachmentsResponseDataApi> => {
    try {
        return await dataAPI.getJSON(
            `Items(${itemId})/Attachments`,
            queryParameters,
            receiver,
        )
    } catch (error) {
        if (error instanceof Response) {
            const errorMessage = error instanceof Error ? error.message : ""
            throw await RequestErrorDataApi.create(
                errorMessage,
                error.status,
                error,
            )
        }
        throw error
    }
}

export const getInlineAttachmentsDataApi = async (
    itemId: number,
    fieldId: string,
    queryParameters?: DataApiQueryOptions,
    receiver?: Receiver,
): Promise<AttachmentsResponseDataApi> => {
    try {
        return await dataAPI.getJSON(
            `ItemFields(itemid=${itemId},fieldDefinitionId='${fieldId}')`,
            queryParameters,
            receiver,
        )
    } catch (error) {
        if (error instanceof Response) {
            const errorMessage = error instanceof Error ? error.message : ""
            throw await RequestErrorDataApi.create(
                errorMessage,
                error.status,
                error,
            )
        }
        throw error
    }
}

export const getAttachmentCreatorDataApi = async (
    attachmentId: number,
    queryParameters?: DataApiQueryOptions,
    receiver?: Receiver,
): Promise<Partial<AttachmentCreatorDTO>> => {
    try {
        return await dataAPI.getJSON(
            `Attachments(${attachmentId})/Creator`,
            queryParameters,
            receiver,
        )
    } catch (error) {
        if (error instanceof Response) {
            const errorMessage = error instanceof Error ? error.message : ""
            throw await RequestErrorDataApi.create(
                errorMessage,
                error.status,
                error,
            )
        }
        throw error
    }
}

export const updateAttachmentValueDataApi = async (
    attachmentId: number,
    body?: Partial<AttachmentDTO>,
    receiver?: Receiver,
): Promise<AttachmentDTO> => {
    try {
        return await dataAPI.patchJSON(
            `Attachments(${attachmentId})`,
            body,
            receiver,
        )
    } catch (error) {
        if (error instanceof Response) {
            const errorMessage = error instanceof Error ? error.message : ""
            throw await RequestErrorDataApi.create(
                errorMessage,
                error.status,
                error,
            )
        }
        throw error
    }
}

export const removeAttachmentDataApi = async (
    itemId: number,
    attachmentId: number,
): Promise<void> => {
    try {
        return await dataAPI.remove(
            `Items(${itemId})/Attachments/${attachmentId}/$ref`,
        )
    } catch (error) {
        if (error instanceof Response) {
            const errorMessage = error instanceof Error ? error.message : ""
            throw await RequestErrorDataApi.create(
                errorMessage,
                error.status,
                error,
            )
        }
        throw error
    }
}
